import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { theme, pageTitleStyle, pageTitleTextStyle, primaryButtonStyle, statutBadgeStyle } from '../../theme';

const BASE = '/api';
function authFetch(url, opts={}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, { ...opts, headers: { ...(opts.headers||{}), ...(token?{Authorization:`Bearer ${token}`}:{}) } });
}

// STATUTS_DOSSIER dérivé du thème centralisé (forme conservée : label/bg/color/dot)
export const STATUTS_DOSSIER = Object.fromEntries(
  Object.entries(theme.statut).map(([k, v]) => [k, { label: v.label, bg: v.bg, color: v.fg, dot: v.dot }])
);

// TYPES resolved via t() inline

export default function OutilSinistre({ procedureId, procedureStatut }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isManager } = useAuth();

  const [dossiers, setDossiers]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  // Form
  const [nomVoyageur, setNomVoyageur]   = useState('');
  const [typeVoyageur, setTypeVoyageur] = useState('particulier');

  async function supprimerDossier(d) {
    if (!window.confirm(t('sinistres.supprimer_dossier_confirm'))) return;
    try {
      const res = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers/${d.id}`, { method:'DELETE' });
      const data = await res.json();
      if (data.error) { alert(data.error); return; }
      setDossiers(prev => prev.filter(x => x.id !== d.id));
    } catch(e) { alert(e.message); }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers`);
      const data = await res.json();
      setDossiers(Array.isArray(data) ? data : []);
    } catch { setDossiers([]); }
    finally { setLoading(false); }
  }, [procedureId]);

  useEffect(() => { if (procedureId) load(); }, [load]);

  async function creerDossier() {
    if (!nomVoyageur.trim()) { setError('Le nom du voyageur est requis'); return; }
    setSaving(true); setError('');
    try {
      const res  = await authFetch(`${BASE}/sinistres/${procedureId}/dossiers`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ nom_voyageur: nomVoyageur.trim(), type_voyageur: typeVoyageur }),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setDossiers(prev => [...prev, data]);
      setShowModal(false); setNomVoyageur(''); setTypeVoyageur('particulier');
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  const isPending = procedureStatut === 'ouverture_pendante';
  const isClosed  = procedureStatut === 'cloturee';

  return (
    <div style={{ marginTop:16 }}>

      {/* Header : titre souligné (style "Ligne") */}
      <div style={{ ...pageTitleStyle, marginBottom:16 }}>
        <span style={pageTitleTextStyle}>{t('sinistres.dossiers_title')}</span>
        <span style={{ fontSize:12, color:theme.textTertiary }}>
          {dossiers.length <= 1 ? t('sinistres.dossiers_count').replace('{n}', dossiers.length) : t('sinistres.dossiers_count_pl').replace('{n}', dossiers.length)}
        </span>
        {!isPending && !isClosed && (
          <button onClick={() => { setShowModal(true); setError(''); setNomVoyageur(''); setTypeVoyageur('particulier'); }}
            style={{ ...primaryButtonStyle, marginLeft:'auto' }}>
            {t('sinistres.ajouter_dossier')}
          </button>
        )}
      </div>

      {/* Alerte si procédure pas encore ouverte */}
      {isPending && (
        <div style={{ background:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:8,
          padding:'12px 16px', fontSize:13, color:'#92400E', textAlign:'center' }}>
          ⏳ La procédure doit être confirmée par un Manager avant de pouvoir enregistrer des dossiers.
        </div>
      )}

      {/* Liste des dossiers */}
      {!isPending && (
        <div>
          {/* En-tête tableau */}
          <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1.8fr 1.4fr 1.2fr 1.2fr 1.2fr 1.8fr 0.5fr',
            gap:'0 12px',
            padding:'10px 12px', borderBottom:`0.5px solid ${theme.borderStrong}`,
            fontSize:10.5, fontWeight:500, color:theme.textTertiary, textTransform:'uppercase', letterSpacing:'.05em' }}>
            <div>{t('sinistres.col_numero_dossier')}</div>
            <div>{t('sinistres.col_voyageur')}</div>
            <div>{t('sinistres.col_souhait')}</div>
            <div style={{textAlign:'right'}}>{t('sinistres.col_montant_total')}</div>
            <div style={{textAlign:'right'}}>{t('sinistres.col_acompte')}</div>
            <div style={{textAlign:'right'}}>{t('sinistres.col_solde')}</div>
            <div>{t('sinistres.col_statut')}</div>
            <div></div>
          </div>

          {loading && (
            <div style={{ padding:20, textAlign:'center', color:'#94A3B8', fontSize:13 }}>
              Chargement…
            </div>
          )}

          {!loading && dossiers.length === 0 && (
            <div style={{ padding:28, textAlign:'center', color:'#94A3B8', fontSize:13 }}>
              {t('sinistres.no_dossiers')}
            </div>
          )}

          {dossiers.map((d) => {
            const st = STATUTS_DOSSIER[d.statut] || STATUTS_DOSSIER.en_attente_informations;
            const fmt = v => v != null ? Number(v).toLocaleString('fr-FR',{minimumFractionDigits:2,maximumFractionDigits:2})+' €' : '—';
            const solde = d.montant_total != null ? d.montant_total - (d.acompte_paye||0) : null;
            const souhait = d.souhait_voyageur;
            const souhaitLabels = {
              remboursement:  t('sinistres.benef_souhait_remboursement'),
              maintien:       t('sinistres.benef_souhait_maintien'),
              maintien_voyage:t('sinistres.benef_souhait_maintien'),
              rapatriement:   t('sinistres.benef_souhait_rapatriement'),
            };
            const souhaitColors = {
              remboursement:  { bg:'#FFF7F2', color:'#C2410C' },
              maintien:       { bg:'#F0FDF4', color:'#15803D' },
              maintien_voyage:{ bg:'#F0FDF4', color:'#15803D' },
              rapatriement:   { bg:'#EFF6FF', color:'#1D4ED8' },
            };
            const sc = souhaitColors[souhait] || { bg:'#F8FAFC', color:'#94A3B8' };
            // Couleurs montants selon souhait
            const isRembours  = souhait === 'remboursement';
            const isMaintien  = souhait === 'maintien' || souhait === 'maintien_voyage';
            const colorAcompte = isRembours  ? '#15803D' : '#475569';
            const colorSolde   = isMaintien  ? '#E8671A' : '#475569';
            return (
              <div key={d.id}
                onClick={() => navigate(`/sinistres/${procedureId}/dossiers/${d.id}`)}
                style={{ display:'grid', gridTemplateColumns:'1.2fr 1.8fr 1.4fr 1.2fr 1.2fr 1.2fr 1.8fr 0.5fr',
                  gap:'0 12px',
                  padding:'12px', borderTop:`0.5px solid ${theme.border}`,
                  alignItems:'center', cursor:'pointer',
                  background:'#fff', transition:'background .15s' }}
                onMouseEnter={e => e.currentTarget.style.background=theme.surfaceAlt}
                onMouseLeave={e => e.currentTarget.style.background='#fff'}>

                {/* N° dossier */}
                <div style={{ fontFamily:'monospace', fontSize:13, fontWeight:500, color:theme.text }}>
                  {d.numero_dossier}
                </div>

                {/* Nom voyageur */}
                <div>
                  <div style={{ fontSize:13, fontWeight:500, color:theme.text }}>{d.nom_voyageur}</div>
                  <div style={{ fontSize:11, color:theme.textTertiary, marginTop:1 }}>
                    {d.type_voyageur === 'professionnel' ? t('sinistres.type_professionnel') : t('sinistres.type_particulier')}
                  </div>
                </div>

                {/* Souhait voyageur */}
                <div>
                  {souhait ? (
                    <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:99,
                      background:sc.bg, color:sc.color }}>
                      {souhaitLabels[souhait] || souhait}
                    </span>
                  ) : <span style={{ color:'#CBD5E1', fontSize:12 }}>—</span>}
                </div>

                {/* Montant total */}
                <div style={{ textAlign:'right', fontSize:13, fontWeight:600, color:'#1A1A1A' }}>
                  {fmt(d.montant_total)}
                </div>

                {/* Acompte payé */}
                <div style={{ textAlign:'right', fontSize:13,
                  fontWeight: isRembours && d.acompte_paye != null ? 700 : 400,
                  color: d.acompte_paye != null ? colorAcompte : '#CBD5E1' }}>
                  {fmt(d.acompte_paye)}
                </div>

                {/* Solde restant */}
                <div style={{ textAlign:'right', fontSize:13,
                  fontWeight: isMaintien && solde != null ? 700 : 400,
                  color: solde != null ? colorSolde : '#CBD5E1' }}>
                  {fmt(solde)}
                </div>

                {/* Statut */}
                <div>
                  <span style={{ ...statutBadgeStyle(d.statut), fontSize:12, padding:'3px 10px' }}>
                    <span style={{ width:7,height:7,borderRadius:'50%',background:st.dot,flexShrink:0 }}/>
                    {t('sinistres.dos_statut_' + d.statut) || st.label}
                  </span>
                </div>

                {/* Flèche + supprimer */}
                <div style={{ display:'flex', gap:4, alignItems:'center', justifyContent:'flex-end' }}>
                  <button
                    onClick={e => { e.stopPropagation(); supprimerDossier(d); }}
                    title={t('sinistres.supprimer_dossier')}
                    style={{ background:'#FFF5F5', border:'1px solid #FCA5A5', borderRadius:6,
                      padding:'4px 8px', cursor:'pointer', fontSize:12, color:'#DC2626' }}>
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Légende statuts */}
      {!isPending && dossiers.length > 0 && (
        <div style={{ marginTop:14, display:'flex', flexWrap:'wrap', gap:8 }}>
          {Object.entries(STATUTS_DOSSIER).map(([k, v]) => (
            <span key={k} style={{ display:'inline-flex', alignItems:'center', gap:5,
              fontSize:11, color:'#64748B' }}>
              <span style={{ width:7,height:7,borderRadius:'50%',background:v.dot,flexShrink:0 }}/>
              {t('sinistres.dos_statut_' + k)}
            </span>
          ))}
        </div>
      )}

      {/* ── Modal : Ajouter un dossier ─────────────────────── */}
      {showModal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',
          alignItems:'center',justifyContent:'center',zIndex:1000 }}>
          <div style={{ background:'#fff',borderRadius:12,padding:26,width:440,maxWidth:'92vw' }}>
            <div style={{ fontSize:16,fontWeight:800,marginBottom:4 }}>{t('sinistres.nouveau_dossier')}</div>
            <div style={{ fontSize:12,color:'#64748B',marginBottom:20 }}>
              Enregistrez un nouveau bénéficiaire de la garantie financière.
            </div>

            {error && (
              <div style={{ background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:7,
                padding:'8px 12px',marginBottom:14,fontSize:13,color:'#991B1B' }}>⚠ {error}</div>
            )}

            {/* Type voyageur */}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:8 }}>
                Type de bénéficiaire
              </label>
              <div style={{ display:'flex', gap:8 }}>
                {[['particulier','👤 Particulier'],['professionnel','🏢 Professionnel']].map(([val,lbl]) => (
                  <button key={val} type="button"
                    onClick={() => setTypeVoyageur(val)}
                    style={{ flex:1, padding:'10px', borderRadius:8, cursor:'pointer',
                      fontSize:13, fontWeight: typeVoyageur===val ? 700 : 400,
                      background: typeVoyageur===val ? '#1A1A1A' : '#F8FAFC',
                      color: typeVoyageur===val ? '#fff' : '#64748B',
                      border:`1.5px solid ${typeVoyageur===val?'#1A1A1A':'#E2E8F0'}` }}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>

            {/* Nom */}
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5 }}>
                {typeVoyageur==='particulier' ? 'Nom / Prénom *' : 'Raison sociale *'}
              </label>
              <input
                autoFocus
                value={nomVoyageur}
                onChange={e => setNomVoyageur(e.target.value)}
                onKeyDown={e => e.key==='Enter' && creerDossier()}
                placeholder={typeVoyageur==='particulier' ? 'Ex. Martin Jean' : 'Ex. SARL Voyages Martin'}
                style={{ border:'1.5px solid #E2E8F0',borderRadius:7,padding:'9px 12px',
                  width:'100%',fontSize:13,boxSizing:'border-box',fontFamily:'inherit' }}/>
            </div>

            <div style={{ display:'flex',justifyContent:'flex-end',gap:8 }}>
              <button onClick={() => setShowModal(false)}
                style={{ fontSize:14,padding:'8px 18px',borderRadius:7,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff' }}>
                Annuler
              </button>
              <button onClick={creerDossier} disabled={!nomVoyageur.trim()||saving}
                style={{ fontSize:14,padding:'8px 18px',borderRadius:7,background:theme.orange,
                  color:'#fff',border:'none',cursor:'pointer',fontWeight:500,
                  opacity:(!nomVoyageur.trim()||saving)?0.5:1 }}>
                {saving ? t('sinistres.creation_dossier') : t('sinistres.creer_dossier')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
